// Apply as damage or healing… (qa-runs#1410: the words say what Apply does) on any roll in the dice log (qa-runs#406; Tom's decision of 2026-10-29): pick one or more tokens this person can
// see, then Damage or Healing (a damage type and Nonlethal optional). The server decides what lands at once and what
// waits for the DM; the log shows where the roll has been applied, and the DM's Apply / Change / Drop for what waits.
import { useState } from 'react';
import { APPLY_LIMITS, applicable, appliedDealt, appliedLine, tokenLabel, typesIn, type Actor, type RollApplied, type RollRecord } from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { BTN } from '../ui';

const btn = BTN;

interface TokenLike { id: string; kind: string; name?: string | null }

/** Whether this viewer may apply this roll: the DM any roll's, a player their own (never one rolled for them). */
export function mayApply(roll: RollRecord, meId: string, isDm: boolean): boolean {
  return applicable(roll) && (isDm || (roll.by === null && roll.actor.id === meId));
}

/** The Apply… button and its form, under a roll in the log. */
export function ApplyRoll({ roll, meId, isDm }: { roll: RollRecord; meId: string; isDm: boolean }) {
  const { store, act, setNotice, systemRules } = useTableState();
  const types = systemRules.damage?.types ?? [];
  const [open, setOpen] = useState(false);
  const [chosen, setChosen] = useState<string[]>([]);
  const [heal, setHeal] = useState(false);
  const [type, setType] = useState('');
  const [nonlethal, setNonlethal] = useState(false);
  if (!mayApply(roll, meId, isDm)) return null;
  const amount = roll.fudged?.total ?? roll.total;
  const done = new Set((roll.applied ?? []).map((a) => a.target));
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token' && !done.has(o.id));
  const nameOf = (o: TokenLike) => tokenLabel(o, store.state.encounter) || 'Unnamed token';
  const close = () => { setOpen(false); setChosen([]); setHeal(false); setType(''); setNonlethal(false); };
  if (!open) return <button type="button" className={btn} data-testid={`apply-${roll.id}`} aria-label={`Apply ${roll.notation} = ${amount} as damage or healing to a token`} title={`Use this ${amount} as damage or healing on the tokens you tick next`} onClick={() => {
    // A spell's roll says its type in its label; the form starts on it.
    const said = typesIn({ types }, roll.label ?? '')[0];
    setType(types.find((t) => t.key === said)?.label.toLowerCase() ?? '');
    setOpen(true);
  }}>Apply as damage or healing…</button>;
  const send = () => {
    act({ kind: 'roll.apply', rollId: roll.id, targets: chosen, heal, ...(!heal && type.trim() ? { type: type.trim() } : {}), ...(!heal && nonlethal ? { nonlethal: true } : {}) } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be applied.'); });
    close();
  };
  return (
    <form className="mt-1 w-full space-y-1 rounded border border-stone-700 p-1.5 font-sans text-xs" data-testid={`apply-form-${roll.id}`} onSubmit={(e) => { e.preventDefault(); if (chosen.length) send(); }}>
      <fieldset className="flex flex-wrap gap-x-3 gap-y-1">
        <legend className="text-stone-400">Tick each token to take {amount} as damage or healing</legend>
        {tokens.length === 0 && <span className="text-stone-500">No token to apply it to.</span>}
        {tokens.map((o) => (
          <label key={o.id} className="flex min-h-[28px] items-center gap-1">
            <input type="checkbox" aria-label={`Apply to ${nameOf(o)}`} checked={chosen.includes(o.id)} disabled={!chosen.includes(o.id) && chosen.length >= APPLY_LIMITS.targets}
              onChange={(e) => setChosen((cur) => (e.target.checked ? [...cur, o.id] : cur.filter((x) => x !== o.id)))} />{nameOf(o)}
          </label>
        ))}
      </fieldset>
      <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Damage or healing">
        <label className="flex min-h-[28px] items-center gap-1"><input type="radio" name={`apply-kind-${roll.id}`} checked={!heal} onChange={() => setHeal(false)} />Damage</label>
        <label className="flex min-h-[28px] items-center gap-1"><input type="radio" name={`apply-kind-${roll.id}`} checked={heal} onChange={() => setHeal(true)} />Healing</label>
      </div>
      {!heal && (
        <div className="flex flex-wrap items-center gap-2">
          {types.length > 0 ? (
            <select aria-label="Damage type" className="rounded bg-stone-800 px-1 py-0.5" value={type} onChange={(e) => setType(e.target.value)}>
              <option value="">No type</option>
              {types.map((t) => <option key={t.key} value={t.label.toLowerCase()}>{t.label}</option>)}
            </select>
          ) : <input aria-label="Damage type" placeholder="Type (fire, cold…), optional" maxLength={APPLY_LIMITS.type} className="w-44 max-w-full rounded bg-stone-800 px-1 py-0.5" value={type} onChange={(e) => setType(e.target.value)} />}
          <label className="flex min-h-[28px] items-center gap-1"><input type="checkbox" checked={nonlethal} onChange={(e) => setNonlethal(e.target.checked)} />Nonlethal</label>
        </div>
      )}
      <div className="flex gap-1">
        <button type="submit" className={btn} disabled={!chosen.length}>Apply {amount} {heal ? 'healing' : 'damage'}</button>
        <button type="button" className={btn} onClick={close}>Cancel</button>
      </div>
    </form>
  );
}

/** Where a roll has been applied (qa-runs#406): one line each; for the DM, Apply / Change / Drop on what waits. */
export function AppliedRows({ roll, nameOf, isDm }: { roll: RollRecord; nameOf: (a: Actor) => string; isDm: boolean }) {
  if (!roll.applied?.length) return null;
  return (
    <ul className="mt-1 space-y-0.5 font-sans text-[0.6875rem]" data-testid={`applied-${roll.id}`}>
      {roll.applied.map((a) => (
        <li key={a.target} data-testid={`applied-${roll.id}-${a.target}`} data-settled={a.settled ? (a.settled.amount === null ? 'dropped' : 'yes') : 'no'}>
          {/* A dropped one stands out (qa-runs#1400), where a settled one reads faint. */}
          <span className={a.settled ? (a.settled.amount === null ? 'font-semibold text-rose-300' : 'text-stone-400') : 'text-amber-300'}>{a.settled?.amount === null && '✕ '}{appliedLine(roll, a, nameOf)}</span>
          {isDm && !a.settled && <SettleApplied rollId={roll.id} a={a} />}
        </li>
      ))}
    </ul>
  );
}

/** The DM's proposal for one application waiting: *6 damage to Goblin 2?* with Apply, Change and Drop, as an attack's. */
function SettleApplied({ rollId, a }: { rollId: string; a: RollApplied }) {
  const { act, setNotice } = useTableState();
  const [changing, setChanging] = useState(false);
  const [value, setValue] = useState('');
  const dealt = appliedDealt(a).total;
  const what = a.heal ? 'healing' : `${a.nonlethal ? 'nonlethal ' : ''}${a.type ? `${a.type} ` : ''}damage`;
  const send = (amount: number | null) => act({ kind: 'roll.settle', rollId, target: a.target, amount } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be applied.'); });
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1" data-testid={`settle-${rollId}-${a.target}`}>
      <span className="text-amber-300">{dealt} {what} to {a.name}?</span>
      <button type="button" className={btn} onClick={() => send(dealt)}>Apply {dealt}</button>
      {!changing && <button type="button" className={btn} onClick={() => { setValue(String(dealt)); setChanging(true); }}>Change…</button>}
      {changing && (
        <form className="flex items-center gap-1" onSubmit={(e) => { e.preventDefault(); const n = Math.trunc(Number(value)); if (value.trim() !== '' && Number.isFinite(n) && n >= 0) send(n); }}>
          <input aria-label={a.heal ? 'Healing to apply' : 'Damage to apply'} type="number" min={0} className="w-14 rounded bg-stone-800 px-1 py-0.5 text-xs" value={value} onChange={(e) => setValue(e.target.value)} />
          <button type="submit" className={btn}>Apply</button>
        </form>
      )}
      <button type="button" className={btn} onClick={() => send(null)}>Drop</button>
    </div>
  );
}
